import { useState, useCallback, useEffect } from 'react'
import { fetchQuery } from 'relay-runtime'
import Env from '@src/RelayEnvironment'
import { graphql } from 'react-relay'
import { useBJFMiningHistoryQuery } from './__generated__/useBJFMiningHistoryQuery.graphql'

export interface MiningHistoryItem {
  miningItemId: number
  miningAmount: number
  payedOut: string | null
  insertedAt: string
  updatedAt: string
  finished: boolean
}

const useBJFMiningHistory = () => {
  const [history, setHistory] = useState<MiningHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [hasMore, setHasMore] = useState(false)
  const [totalPages, setTotalPages] = useState(0)

  const fetchHistory = useCallback(
    (currentPage = 1) => {
      if (isLoading) return Promise.resolve()

      setIsLoading(true)

      return fetchQuery<useBJFMiningHistoryQuery>(
        Env,
        bjfMiningHistoryQuery,
        {
          page: currentPage,
          pageSize,
          filter: {
            sortOrder: 'desc',
            sortBy: 'id'
          }
        },
        { fetchPolicy: 'network-only' }
      ).subscribe({
        next: (data) => {
          const { getBjfMiningHistory } = data
          
          if (!getBjfMiningHistory) {
            setIsLoading(false)
            return
          }

          const entries = getBjfMiningHistory.entries.map(entry => ({
            miningItemId: entry.miningItemId,
            miningAmount: entry.miningAmount,
            payedOut: entry.payedOut,
            insertedAt: entry.insertedAt,
            updatedAt: entry.updatedAt,
            finished: entry.finished
          }))

          setHistory((prevHistory) => {
            if (currentPage === 1) {
              return [...entries]
            }
            return [...prevHistory, ...entries]
          })

          setTotalPages(getBjfMiningHistory.totalPages || 0)
          setHasMore(currentPage < (getBjfMiningHistory.totalPages || 0))
          setIsLoading(false)
        },
        error: (err) => {
          console.error('Error fetching BJF mining history:', err)
          setIsLoading(false)
          setHasMore(false)
        },
        complete: () => {
          setIsLoading(false)
        },
      })
    },
    [isLoading, pageSize]
  )

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return

    const nextPage = page + 1
    if (nextPage <= totalPages) {
      setPage(nextPage)
      fetchHistory(nextPage)
    } else {
      setHasMore(false)
    }
  }, [hasMore, isLoading, page, totalPages, fetchHistory])

  // Reset to page 1 and refetch
  const refreshHistory = useCallback(() => {
    setPage(1)
    fetchHistory(1)
  }, [])

  // Subscribe to refresh events
  useEffect(() => {
    historyRefreshListeners.push(refreshHistory);
    
    // Cleanup on unmount
    return () => {
      const index = historyRefreshListeners.indexOf(refreshHistory);
      if (index > -1) {
        historyRefreshListeners.splice(index, 1);
      }
    };
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchHistory(1)
  }, [])

  return {
    history,
    isLoading,
    hasMore,
    loadMore,
    page,
    refreshHistory
  }
}

export default useBJFMiningHistory 

const bjfMiningHistoryQuery = graphql`
  query useBJFMiningHistoryQuery($page: Int!, $pageSize: Int, $filter: GetBjfMiningHistoryFilter) {
    getBjfMiningHistory(page: $page, pageSize: $pageSize, filter: $filter) {
      entries {
        miningItemId: id
        miningAmount
        payedOut
        insertedAt
        updatedAt
        finished
      }
      pageNumber
      pageSize
      totalPages
      totalEntries
    }
  }
`

// Create a simple event system to notify subscribers when history should be refreshed
const historyRefreshListeners: (() => void)[] = [];

// Standalone function to refresh history
export const refreshBJFMiningHistory = () => {
  const result = fetchQuery<useBJFMiningHistoryQuery>(
    Env,
    bjfMiningHistoryQuery,
    {
      page: 1,
      pageSize: 10,
      filter: {
        sortOrder: 'desc',
        sortBy: 'id'
      }
    }
  )
  
  // Notify all listeners that they should refresh
  historyRefreshListeners.forEach(listener => listener());
  
  return result;
}
